import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  InputAdornment,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ScienceIcon from '@mui/icons-material/Science';
import BarChartIcon from '@mui/icons-material/BarChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TokenIcon from '@mui/icons-material/Token';
import SpeedIcon from '@mui/icons-material/Speed';
import PsychologyIcon from '@mui/icons-material/Psychology';

import {
  getAiConfig,
  saveAiConfig,
  getAiQueryHistory,
  clearAiQueryHistory,
  getAiConsumptionMetrics,
  calculateTokenCost,
  AVAILABLE_MINIMAX_MODELS,
} from '../services/aiConfigService';
import { testMiniMaxConnection } from '../services/minimaxService';

const AiParametersDialog = ({ open, onClose, onConfigSaved }) => {
  const [activeTab, setActiveTab] = useState(0);

  // Estados de configuración
  const [config, setConfig] = useState(getAiConfig());
  const [showApiKey, setShowApiKey] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Estados del Playground
  const [testPrompt, setTestPrompt] = useState('Hi, how are you?');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);

  // Estados de métricas e historial
  const [history, setHistory] = useState([]);
  const [metrics, setMetrics] = useState(null);

  // Cargar datos al abrir el diálogo
  useEffect(() => {
    if (open) {
      const currentConfig = getAiConfig();
      setConfig(currentConfig);
      refreshMetrics();
      setSaveSuccess(false);
      setTestResult(null);
      setTestError(null);
    }
  }, [open]);

  const refreshMetrics = () => {
    setHistory(getAiQueryHistory());
    setMetrics(getAiConsumptionMetrics());
  };

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveConfig = () => {
    saveAiConfig(config);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    if (onConfigSaved) {
      onConfigSaved(config);
    }
  };

  const handleRunPlaygroundTest = async () => {
    setTestLoading(true);
    setTestResult(null);
    setTestError(null);

    try {
      if (!config.minimaxApiKey?.trim()) {
        throw new Error('Debes ingresar y guardar una API Key de MiniMax en la pestaña de Configuración.');
      }

      const result = await testMiniMaxConnection(testPrompt, config);
      const cost = calculateTokenCost(result.usage.inputTokens, result.usage.outputTokens, config);

      setTestResult({
        ...result,
        cost,
      });
      refreshMetrics();
    } catch (err) {
      console.error('Error en playground MiniMax:', err);
      setTestError(err.message || 'Error al conectar con la API de MiniMax');
    } finally {
      setTestLoading(false);
    }
  };

  const handleClearHistory = () => {
    if (window.confirm('¿Deseas vaciar todo el historial de consumo de tokens? Esta acción no se puede deshacer.')) {
      clearAiQueryHistory();
      refreshMetrics();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8f9ff', pb: 1 }}>
        <SettingsIcon sx={{ color: '#6164ff' }} />
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, color: '#2d3748' }}>
          Módulo de Parámetros de IA & Control de Tokens (MiniMax-M2.5)
        </Typography>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, bgcolor: '#f8f9ff' }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab icon={<SettingsIcon fontSize="small" />} iconPosition="start" label="Parámetros & Tarifas" />
          <Tab icon={<ScienceIcon fontSize="small" />} iconPosition="start" label="Playground / Probador" />
          <Tab icon={<BarChartIcon fontSize="small" />} iconPosition="start" label="Métricas & Consumo" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* PESTAÑA 1: CONFIGURACIÓN Y PARÁMETROS */}
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {saveSuccess && (
              <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                Configuración y tarifas guardadas exitosamente en tu almacenamiento local.
              </Alert>
            )}

            <Card variant="outlined" sx={{ bgcolor: '#fafbff', borderColor: '#c5cae9' }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#3949ab', mb: 1 }}>
                  🤖 Proveedor de IA Activo
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="provider-label">Motor de Inteligencia Artificial</InputLabel>
                      <Select
                        labelId="provider-label"
                        value={config.provider}
                        label="Motor de Inteligencia Artificial"
                        onChange={(e) => handleConfigChange('provider', e.target.value)}
                      >
                        <MenuItem value="minimax">
                          <strong>MiniMax (MiniMax-M2.5 / Anthropic API)</strong>
                        </MenuItem>
                        <MenuItem value="gemini">Google Gemini (gemini-3.1-flash-lite)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      {config.provider === 'minimax'
                        ? '🟢 MiniMax activo: Se medirá el consumo de tokens y costo por cada análisis de actas.'
                        : 'Google Gemini activo por defecto con cuota estándar.'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2d3748', mt: 1 }}>
              ⚙️ Parámetros del Modelo MiniMax
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="model-label">Modelo MiniMax</InputLabel>
                  <Select
                    labelId="model-label"
                    value={config.minimaxModel}
                    label="Modelo MiniMax"
                    onChange={(e) => handleConfigChange('minimaxModel', e.target.value)}
                  >
                    {AVAILABLE_MINIMAX_MODELS.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="API Key de MiniMax"
                  type={showApiKey ? 'text' : 'password'}
                  value={config.minimaxApiKey}
                  onChange={(e) => handleConfigChange('minimaxApiKey', e.target.value)}
                  placeholder="Ingresa tu API Key de MiniMax"
                  helperText="Se almacena solo en el navegador para pruebas locales"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowApiKey(!showApiKey)}>
                          {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Base URL API"
                  value={config.minimaxBaseUrl}
                  onChange={(e) => handleConfigChange('minimaxBaseUrl', e.target.value)}
                  helperText="Usa '/api/minimax' (recomendado con proxy local) o 'https://api.minimax.io/anthropic'"
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Max Tokens"
                  value={config.maxTokens}
                  onChange={(e) => handleConfigChange('maxTokens', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ step: '0.05', min: '0', max: '1' }}
                  label="Temperature"
                  value={config.temperature}
                  onChange={(e) => handleConfigChange('temperature', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2d3748' }}>
              💵 Tarifas de Tokens y Cálculo de Costos
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  label="Costo Entrada (USD / 1M tokens)"
                  value={config.inputPricePerMillion}
                  onChange={(e) => handleConfigChange('inputPricePerMillion', e.target.value)}
                  helperText="Ej: $0.15 USD por 1M tokens"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  label="Costo Salida (USD / 1M tokens)"
                  value={config.outputPricePerMillion}
                  onChange={(e) => handleConfigChange('outputPricePerMillion', e.target.value)}
                  helperText="Ej: $1.10 USD por 1M tokens"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Tasa de cambio COP / USD"
                  value={config.copExchangeRate}
                  onChange={(e) => handleConfigChange('copExchangeRate', e.target.value)}
                  helperText="Para calcular costo estimado en pesos"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                sx={{ bgcolor: '#6164ff', '&:hover': { bgcolor: '#4f52e0' }, px: 3 }}
              >
                Guardar Parámetros
              </Button>
            </Box>
          </Box>
        )}

        {/* PESTAÑA 2: PLAYGROUND / PROBADOR RÁPIDO */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Prueba la conexión y la estructura de llamada a <strong>{config.minimaxModel}</strong> compatible con
              Anthropic Messages API. Verás los bloques de respuesta de pensamiento (thinking), texto y conteo exacto de
              tokens y costo.
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Mensaje de prueba para el modelo"
              value={testPrompt}
              onChange={(e) => setTestPrompt(e.target.value)}
              placeholder="Ej: Hi, how are you?"
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleRunPlaygroundTest}
                disabled={testLoading}
                startIcon={testLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' }, fontWeight: 600 }}
              >
                {testLoading ? 'Consultando a MiniMax...' : 'Ejecutar Consulta de Prueba'}
              </Button>
            </Box>

            {testError && <Alert severity="error">{testError}</Alert>}

            {testResult && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {/* Tarjeta de métricas de la consulta */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: '#f1f8e9',
                    borderColor: '#81c784',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tokens Entrada
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {testResult.usage.inputTokens.toLocaleString()}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tokens Salida
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {testResult.usage.outputTokens.toLocaleString()}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Tokens
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1b5e20' }}>
                      {testResult.usage.totalTokens.toLocaleString()}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Costo USD
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#e65100' }}>
                      {testResult.cost.formattedUsd}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Costo Estimado COP
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#e65100' }}>
                      {testResult.cost.formattedCop}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tiempo Respuesta
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#37474f' }}>
                      {testResult.durationMs} ms
                    </Typography>
                  </Box>
                </Paper>

                {/* Bloque de Thinking si existe */}
                {testResult.thinking && (
                  <Card variant="outlined" sx={{ bgcolor: '#fffde7', borderColor: '#fff59d' }}>
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PsychologyIcon sx={{ color: '#f57f17' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f57f17' }}>
                          Pensamiento del Modelo (Thinking Block)
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: '#424242',
                          m: 0,
                        }}
                      >
                        {testResult.thinking}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Bloque de Texto */}
                <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#424242', mb: 1 }}>
                      Respuesta del Modelo (Text Block)
                    </Typography>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'sans-serif',
                        fontSize: '0.9rem',
                        color: '#212121',
                        m: 0,
                      }}
                    >
                      {testResult.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        )}

        {/* PESTAÑA 3: MÉTRICAS E HISTORIAL DE CONSUMO */}
        {activeTab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Tarjetas de Resumen */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9ff', textAlign: 'center' }}>
                  <SpeedIcon sx={{ color: '#6164ff', mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Total Consultas
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d3748' }}>
                    {metrics?.totalQueries || 0}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0fdf4', textAlign: 'center' }}>
                  <TokenIcon sx={{ color: '#16a34a', mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Total Tokens Usados
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#15803d' }}>
                    {(metrics?.totalTokens || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbeb', textAlign: 'center' }}>
                  <MonetizationOnIcon sx={{ color: '#d97706', mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Costo Total USD
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#b45309' }}>
                    ${(metrics?.totalCostUsd || 0).toFixed(4)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fef2f2', textAlign: 'center' }}>
                  <MonetizationOnIcon sx={{ color: '#dc2626', mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Costo Total COP
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#b91c1c' }}>
                    ${Math.round(metrics?.totalCostCop || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2d3748' }}>
                Historial de Consultas Locales
              </Typography>
              {history.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleClearHistory}
                >
                  Limpiar Historial
                </Button>
              )}
            </Box>

            {history.length === 0 ? (
              <Alert severity="info">Aún no hay consultas registradas en este navegador.</Alert>
            ) : (
              <Box sx={{ maxHeight: 350, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha / Hora</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Documento</TableCell>
                      <TableCell>Modelo</TableCell>
                      <TableCell align="right">In Tokens</TableCell>
                      <TableCell align="right">Out Tokens</TableCell>
                      <TableCell align="right">Costo USD</TableCell>
                      <TableCell align="right">Costo COP</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {new Date(item.timestamp).toLocaleString('es-CO', {
                            dateStyle: 'short',
                            timeStyle: 'medium',
                          })}
                        </TableCell>
                        <TableCell>
                          <Chip label={item.queryType} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.fileName}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{item.model}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                          {(item.inputTokens || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                          {(item.outputTokens || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b45309' }}>
                          ${(item.costUsd || 0).toFixed(5)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b91c1c' }}>
                          ${Math.round(item.costCop || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.success ? 'OK' : 'Error'}
                            size="small"
                            color={item.success ? 'success' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f8f9ff' }}>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiParametersDialog;
